import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import ArialNormal from "../../../../utils/Customfonts/ARIAL.TTF";
import ArialBold from "../../../../utils/Customfonts/ARIALBD.TTF";
import ArialItalic from "../../../../utils/Customfonts/ARIALI.TTF";
import ArialBoldItalic from "../../../../utils/Customfonts/ARIALBI.TTF";

import { TECHNICAL_COMPETENCY_DATA } from "./technicalCompetencyRecord";

const DYC_LOGO = "/img/dycgpl-logo.webp";

export const customFontFamily = {
  bold: "bold",
  normal: "normal",
  boldFont: "Arial",
  normalFont: "Arial",
  italic: "italic",
  italicFont: "Arial",
};

// -----------------------------------------------------------------------------
// GLOBAL LAYOUT CONFIG (Landscape Orientation for Wide 7-Column Table)
// -----------------------------------------------------------------------------

export const config = {
  borderMargin: 15,
  contentPadding: 16,

  get margin() {
    return this.borderMargin + this.contentPadding;
  },

  fontSize: 8.0,
  fontSizeSm: 7.0,
  fontSizeTitle: 13.0,

  cellPadding: 3.5,
  sectionGap: 10,
  lineGap: 12,

  headerFontSize: 7.5,
  headerFont: customFontFamily.boldFont,
  headerFontStyle: customFontFamily.bold,
  headerAlign: "center",

  get contentTop() {
    return this.borderMargin + 50;
  },

  get contentBottom() {
    return this.borderMargin + 25;
  },
};

// -----------------------------------------------------------------------------
// COMMON STYLES
// -----------------------------------------------------------------------------

const getBaseStyles = () => ({
  fontSize: config.fontSizeSm,
  cellPadding: config.cellPadding,
  lineColor: [0, 0, 0],
  lineWidth: 0.5,
  textColor: [0, 0, 0],
  fillColor: false,
  font: customFontFamily.normalFont,
  fontStyle: customFontFamily.normal,
  halign: "left",
  valign: "middle",
});

const getHeaderStyles = () => ({
  ...getBaseStyles(),
  font: customFontFamily.boldFont,
  fontStyle: customFontFamily.bold,
  fillColor: [225, 230, 240],
  textColor: [0, 0, 0],
  halign: "center",
  valign: "middle",
});

// -----------------------------------------------------------------------------
// LAYOUT MANAGER
// -----------------------------------------------------------------------------

const layout = {
  doc: null,
  y: 0,
  pageWidth: 0,
  pageHeight: 0,
  contentWidth: 0,

  init(doc) {
    this.doc = doc;
    this.pageWidth = doc.internal.pageSize.getWidth();
    this.pageHeight = doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - config.margin * 2;
    this.y = config.contentTop;
  },

  advance(amount) {
    this.y += amount;
  },

  ensureSpace(requiredHeight = 40) {
    if (this.y + requiredHeight > this.pageHeight - config.contentBottom) {
      this.doc.addPage();
      this.y = config.contentTop;
    }
  },

  afterTable(gap = config.sectionGap) {
    if (this.doc.lastAutoTable) {
      this.y = this.doc.lastAutoTable.finalY + gap;
    }
  },
};

// -----------------------------------------------------------------------------
// FONT LOADER
// -----------------------------------------------------------------------------

const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const loadAndAddFont = async (pdfDoc, fontUrl, fontNameTTF, fontName, fontStyle) => {
  try {
    const response = await fetch(fontUrl);
    const buffer = await response.arrayBuffer();
    const base64 = arrayBufferToBase64(buffer);

    pdfDoc.addFileToVFS(`${fontNameTTF}.ttf`, base64);
    pdfDoc.addFont(`${fontNameTTF}.ttf`, fontName, fontStyle);
  } catch (error) {
    console.error(`Font loading failed: ${fontName}`, error);
  }
};

// -----------------------------------------------------------------------------
// PAGE DECORATORS (Outer Border, Header Line, Logo, Footers on EVERY page)
// -----------------------------------------------------------------------------

const addPageDecorators = (doc, totalPages, data = {}) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);

    // 1. OUTER BORDER
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.7);
    doc.rect(
      config.borderMargin,
      config.borderMargin,
      pageWidth - config.borderMargin * 2,
      pageHeight - config.borderMargin * 2
    );

    // 2. HEADER TITLE (Centered)
    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.setFontSize(13.5);
    doc.setTextColor(20, 20, 20);
    doc.text(
      "TECHNICAL COMPETENCY RECORD",
      pageWidth / 2,
      config.borderMargin + 26,
      { align: "center" }
    );

    // 3. LOGO (Top Right)
    try {
      const logoSize = 34;
      const logoX = pageWidth - config.borderMargin - logoSize - 8;
      const logoY = config.borderMargin + 5;

      try {
        doc.addImage(DYC_LOGO, "WEBP", logoX, logoY, logoSize, logoSize);
      } catch (_) {
        try {
          doc.addImage(DYC_LOGO, "PNG", logoX, logoY, logoSize, logoSize);
        } catch (__) {
          doc.addImage(DYC_LOGO, logoX, logoY, logoSize, logoSize);
        }
      }
    } catch (_) {
      // Optional logo
    }

    // 4. HEADER LINE
    doc.setLineWidth(0.5);
    doc.line(
      config.borderMargin,
      config.borderMargin + 42,
      pageWidth - config.borderMargin,
      config.borderMargin + 42
    );

    // 5. FOOTER (2 Dynamic Lines + Page Count)
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
    doc.setFontSize(7.5);
    doc.setTextColor(40, 40, 40);

    const docCode = `${data.docCode || "DYC/FMT-MR/03"} Rev.: ${data.revNo || "00"}, Eff.Dated -${data.effDate || "24/05/2023"}`;
    const footerY1 = pageHeight - config.borderMargin - 13;
    const footerY2 = pageHeight - config.borderMargin - 5;

    // Line 1 Left
    doc.text(docCode, config.borderMargin + 8, footerY1);

    // Line 2 Left
    doc.text(
      "Company General Use. All Rights Reserved by DYC Global Pvt Ltd.",
      config.borderMargin + 8,
      footerY2
    );

    // Right Page Number
    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.text(`Page ${page} of ${totalPages}`, pageWidth - config.borderMargin - 55, footerY1);
  }
};

// -----------------------------------------------------------------------------
// MAIN DRAWERS
// -----------------------------------------------------------------------------

const drawCandidateHeader = (doc, data) => {
  const contentW = layout.contentWidth;
  const leftX = config.margin;

  const headerRows = [
    [
      {
        content: `Name of Surveyor:  ${data.candidateName || ""}`,
        styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, cellWidth: contentW * 0.55 },
      },
      {
        content: `Office Location:  ${data.officeLocation || ""}`,
        styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, cellWidth: contentW * 0.45 },
      },
    ],
  ];

  autoTable(doc, {
    startY: layout.y,
    body: headerRows,
    theme: "grid",
    styles: { ...getBaseStyles(), fontSize: config.fontSize, fillColor: [248, 250, 252], minCellHeight: 18 },
    columnStyles: {
      0: { cellWidth: contentW * 0.55 },
      1: { cellWidth: contentW * 0.45 },
    },
    margin: { top: config.contentTop, bottom: config.contentBottom, left: leftX, right: leftX },
  });

  layout.afterTable(6);
};

const drawCompetencyTable = (doc, data) => {
  const contentW = layout.contentWidth;
  const leftX = config.margin;
  const rowVals = data.rowValues || {};

  const tableBody = [];

  TECHNICAL_COMPETENCY_DATA.forEach((group) => {
    group.rows.forEach((row, rIdx) => {
      const cellVal = rowVals[row.key] || { authLevel: "", basis: "" };
      const auth = cellVal.authLevel || "";
      const basis = cellVal.basis || "";

      const rowCells = [];

      // 0: IAF Scope (Rowspan)
      if (rIdx === 0) {
        rowCells.push({
          content: group.iafScope,
          rowSpan: group.rows.length,
          styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, halign: "center", valign: "middle", fillColor: [245, 247, 250] },
        });
      }

      // 1: Inspection Scope
      rowCells.push({
        content: row.scope,
        styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold },
      });

      // 2: Category / Range of Inspection
      rowCells.push({
        content: row.category,
      });

      // 3: Authorization Level
      rowCells.push({
        content: auth,
        styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold },
      });

      // 4: Minimum Educational Qualification (Rowspan)
      if (rIdx === 0) {
        rowCells.push({
          content: group.minEdu,
          rowSpan: group.rows.length,
          styles: { valign: "middle", fillColor: [250, 251, 253] },
        });
      }

      // 5: Minimum Requirement (Rowspan)
      if (rIdx === 0) {
        rowCells.push({
          content: group.minReq,
          rowSpan: group.rows.length,
          styles: { valign: "middle", fillColor: [250, 251, 253] },
        });
      }

      // 6: Basis of Authorization
      rowCells.push({
        content: basis,
        styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold },
      });

      tableBody.push(rowCells);
    });
  });

  autoTable(doc, {
    startY: layout.y,
    head: [
      [
        { content: "IAF Scope", styles: { halign: "center" } },
        { content: "Inspection Scope", styles: { halign: "center" } },
        { content: "Category / Range of Inspection", styles: { halign: "center" } },
        { content: "Authorization Level", styles: { halign: "center" } },
        { content: "Minimum Educational Qualification", styles: { halign: "center" } },
        { content: "Minimum Requirement for Level Grade B", styles: { halign: "center" } },
        { content: "Basis of Authorization", styles: { halign: "center" } },
      ],
    ],
    body: tableBody,
    theme: "grid",
    styles: { ...getBaseStyles(), fontSize: config.fontSizeSm },
    headStyles: getHeaderStyles(),
    columnStyles: {
      0: { cellWidth: contentW * 0.11, halign: "center" },
      1: { cellWidth: contentW * 0.16 },
      2: { cellWidth: contentW * 0.31 },
      3: { cellWidth: contentW * 0.08, halign: "center" },
      4: { cellWidth: contentW * 0.14 },
      5: { cellWidth: contentW * 0.12 },
      6: { cellWidth: contentW * 0.08, halign: "center" },
    },
    margin: { top: config.contentTop, bottom: config.contentBottom, left: leftX, right: leftX },
  });

  layout.afterTable(8);
};

const drawLegendsAndSignatures = (doc, data) => {
  const contentW = layout.contentWidth;
  const leftX = config.margin;

  const prepName = data.preparedByName ? data.preparedByName : "";
  const prepDate = data.preparedByDate ? `Date :  ${data.preparedByDate}` : "Date : ___________________________";
  const prepContent = `Prepared by :  ${prepName}\n\n${prepDate}`;

  const appName = data.approvedByName ? data.approvedByName : "";
  const appDate = data.approvedByDate ? `Date :  ${data.approvedByDate}` : "Date : ___________________________";
  const appContent = `Approved by :  ${appName}\n\n${appDate}`;

  const notesRows = [
    [
      {
        content: "LEGENDS & BASIS OF AUTHORIZATION :",
        colSpan: 2,
        styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fillColor: [230, 235, 245], fontSize: 8.0 },
      },
    ],
    [
      {
        content: "Level of Authorization:\n• Grade A - Proficient in training, monitoring, guiding, and evaluating fellow inspectors\n• Grade B - Competent to work autonomously and provide guidance to others\n• Grade C - Capable of working independently under senior guidance\n• Grade D - Able to perform tasks under supervision",
        styles: { cellWidth: contentW * 0.55, fontSize: 6.8, fillColor: [250, 252, 255] },
      },
      {
        content: "Basis of Authorization:\n• I: Evaluation of CV\n• II: Interview\n• III: Monitored inspection\n• IV: Written examination",
        styles: { cellWidth: contentW * 0.45, fontSize: 6.8, fillColor: [250, 252, 255] },
      },
    ],
    [
      {
        content: prepContent,
        styles: { cellWidth: contentW * 0.55, minCellHeight: 40, font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, valign: "top", cellPadding: 5 },
      },
      {
        content: appContent,
        styles: { cellWidth: contentW * 0.45, minCellHeight: 40, font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, valign: "top", cellPadding: 5 },
      },
    ],
  ];

  autoTable(doc, {
    startY: layout.y,
    body: notesRows,
    theme: "grid",
    styles: { ...getBaseStyles() },
    columnStyles: {
      0: { cellWidth: contentW * 0.55 },
      1: { cellWidth: contentW * 0.45 },
    },
    margin: { top: config.contentTop, bottom: config.contentBottom, left: leftX, right: leftX },
  });

  layout.afterTable(6);
};

// -----------------------------------------------------------------------------
// MAIN GENERATOR ENTRY POINT
// -----------------------------------------------------------------------------

export const generateTechnicalCompetencyPDF = async (data = {}) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
    compress: true,
  });

  await Promise.all([
    loadAndAddFont(doc, ArialNormal, "Arial-Normal", customFontFamily.normalFont, customFontFamily.normal),
    loadAndAddFont(doc, ArialBold, "Arial-Bold", customFontFamily.boldFont, customFontFamily.bold),
    loadAndAddFont(doc, ArialItalic, "Arial-Italic", customFontFamily.italicFont, customFontFamily.italic),
    loadAndAddFont(doc, ArialBoldItalic, "Arial-BoldItalic", customFontFamily.normalFont, "bolditalic"),
  ]);

  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);

  layout.init(doc);
  layout.y = config.contentTop + 4;

  drawCandidateHeader(doc, data);
  drawCompetencyTable(doc, data);
  drawLegendsAndSignatures(doc, data);

  const totalPages = doc.internal.getNumberOfPages();
  addPageDecorators(doc, totalPages, data);

  return doc;
};

export default generateTechnicalCompetencyPDF;
